import React, { useState, useEffect, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { styled } from '@mui/material/styles';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import dayjs from 'dayjs';
import { useLanguage } from '../../hooks/useLanguage';

// 스타일 수정 주의!!!! > 클래스이름 가변적
const TimePickerWrapper = styled('div')({
  flex: 1,
  position: 'relative',
  '& .time-input': {
    width: '100%',
    padding: '8.5px 14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    fontSize: '14px',
  },
});

const TimeGridContainer = styled('div')(({ isMobile }) => ({
  padding: '20px',
  backgroundColor: '#fff',
  ...(!isMobile && {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1000,
  }),
  '& h2': {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: 'bold',
  }
}));

const TimeGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '6px 6px',
  maxHeight: '400px',
  overflowY: 'auto',
  '& button': {
    padding: '10px 6px',
    border: 'none',
    borderRadius: '50px',
    background: '#fff',
    cursor: 'pointer',
    '&.selected': {
      backgroundColor: '#FFD44E',
      color: '#000',
    },
    '&:disabled': {
      backgroundColor: '#fff',
      color: '#BBB',
      cursor: 'not-allowed',
    }
  }
});

const TimePicker = ({ 
  isDisabled = false,
  onTimeChange,
  value = '',
  storeInfo = null,
  compareDate = null,
  compareTime = null,
  isTodayCheck = false,
  isReturn = false,
  rentalDateTime = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const wrapperRef = useRef(null);
  const { t } = useLanguage();

  // 모바일 체크
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // PC에서 외부 클릭시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (!isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  const timeOptions = Array.from({ length: 25 }, (_, i) => {
    return `${String(i).padStart(2, '0')}시`;
  });

  const handleTimeSelect = (time) => {
    if (onTimeChange) {
      onTimeChange(time);
    }
    setIsOpen(false);
  };

  const isTimeDisabled = (hour) => {
    if (!storeInfo) return true;
  
    const timeHour = parseInt(hour);
    const startHour = parseInt(storeInfo.startHour);
    const endHour = parseInt(storeInfo.endHour);

    let minSelectableHour = startHour;
    const now = new Date();
    const currentHour = now.getHours();

    // 오늘 날짜일 때만 현재시간+2 제한
    if (isTodayCheck && compareDate && compareDate.isSame(dayjs(), 'date')) {
      minSelectableHour = Math.max(minSelectableHour, currentHour + 2);
    }

    // 날짜가 같을 때만 대여시간+1 제한
    if (
      isReturn &&
      compareTime &&
      compareDate &&
      rentalDateTime &&
      compareDate.isSame(rentalDateTime.date, 'date')
    ) {
      const rentalHour = parseInt(compareTime.replace('시', ''));
      minSelectableHour = Math.max(minSelectableHour, rentalHour + 1);
    }

    // endHour가 00인 경우 24시로 간주
    const adjustedEndHour = endHour === 0 ? 24 : endHour;
    
    return timeHour < minSelectableHour || timeHour > adjustedEndHour;
  };

  const TimeSelector = () => (
    <TimeGridContainer isMobile={isMobile}>
      {isMobile}
      <h2>시간 선택</h2>
      <TimeGrid>
        {timeOptions.map((time) => {
          const hour = time.replace('시', '');
          return (
            <button
              key={time}
              onClick={() => handleTimeSelect(time)}
              className={value === time ? 'selected' : ''}
              disabled={isTimeDisabled(hour)}
            >
              {time}
            </button>
          );
        })}
      </TimeGrid>
    </TimeGridContainer>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePickerWrapper ref={wrapperRef}>
        <button
          className="time-input"
          onClick={() => !isDisabled && setIsOpen(true)}
          disabled={isDisabled}
        >
          {value ? (
            <span className='select-time'>{value}</span>
          ) : (
            <span className='placeholder'>{t('select_time')}</span>
          )}
        </button>

        {isMobile ? (
          <BottomSheet
            open={isOpen}
            onDismiss={() => setIsOpen(false)}
            snapPoints={({ maxHeight }) => [maxHeight * 0.4]}
          >
            <TimeSelector />
          </BottomSheet>
        ) : (
          isOpen && <TimeSelector />
        )}
      </TimePickerWrapper>
    </LocalizationProvider>
  );
};

export default TimePicker;





